/**
 * useMentionInContentEditable
 *
 * Adds @mention autocomplete to a contentEditable element.
 * When the user types "@" followed by characters, a dropdown appears.
 * Selecting a member inserts a mention chip: <span data-mention-id="X">@Name</span>
 *
 * Usage:
 *   const { mentionDropdown, handleMentionKeyDown, handleMentionInput, MentionDropdown } =
 *     useMentionInContentEditable(editorRef, members);
 *
 *   Wire into contentEditable:
 *     onKeyDown={handleMentionKeyDown}
 *     onInput={handleMentionInput}    (or onKeyUp)
 *
 *   Render <MentionDropdown /> as a sibling of the editor (inside a position:relative wrapper)
 *
 * Text extraction:
 *   When serialising the editor, convert mention spans to @[userId:Name] tokens.
 *   Use extractTextWithMentions(el) exported below.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { memberDisplayName } from '../lib/format';

// ── Serialise contentEditable to storage string ───────────────────────────────
export function extractTextWithMentions(el) {
  if (!el) return '';
  let out = '';
  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += node.textContent;
    } else if (node.nodeName === 'BR') {
      out += '\n';
    } else if (node.nodeName === 'DIV') {
      if (out.length && !out.endsWith('\n')) out += '\n';
      node.childNodes.forEach(walk);
    } else if (node.dataset?.mentionId) {
      out += `@[${node.dataset.mentionId}:${node.dataset.mentionName || node.textContent.slice(1)}]`;
    } else {
      node.childNodes.forEach(walk);
    }
  };
  el.childNodes.forEach(walk);
  return out.replace(/^\n|\n$/g, '');
}

// ── Get text before cursor ────────────────────────────────────────────────────
function getTextBeforeCursor(el) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return '';
  const range = sel.getRangeAt(0).cloneRange();
  // Use innerText + selection offset as a reliable fallback
  try {
    range.setStart(el, 0);
    const text = range.toString();
    if (text || el.innerText === '') return text;
  } catch (_) {}
  // Fallback: walk the DOM to collect text up to the cursor node
  try {
    const cursorNode = sel.getRangeAt(0).startContainer;
    const cursorOffset = sel.getRangeAt(0).startOffset;
    let out = '';
    const walk = (node) => {
      if (node === cursorNode) {
        out += (node.textContent || '').slice(0, cursorOffset);
        return true; // stop
      }
      if (node.nodeType === Node.TEXT_NODE) { out += node.textContent; return false; }
      for (const child of node.childNodes) { if (walk(child)) return true; }
      return false;
    };
    walk(el);
    return out;
  } catch (_) {
    return '';
  }
}

// ── Insert mention at cursor ──────────────────────────────────────────────────
function insertMentionAtCursor(member, atOffset, el) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;

  const name = memberDisplayName(member.first_name, member.last_name, member.email);
  const chip = document.createElement('span');
  chip.className = 'mention-chip';
  chip.contentEditable = 'false';
  chip.dataset.mentionId = String(member.user_id);
  chip.dataset.mentionName = name;
  chip.textContent = `@${name}`;

  const textBefore = getTextBeforeCursor(el);
  const atPos = textBefore.lastIndexOf('@');
  if (atPos < 0) return;
  const charsToDelete = textBefore.length - atPos;

  // Collapse selection to cursor, then extend backward to cover @query
  sel.collapseToEnd();
  for (let i = 0; i < charsToDelete; i++) {
    sel.modify('extend', 'backward', 'character');
  }
  sel.getRangeAt(0).deleteContents();
  const insertRange = sel.getRangeAt(0);
  insertRange.insertNode(chip);
  // Insert a trailing space text node
  const space = document.createTextNode(' ');
  insertRange.setStartAfter(chip);
  insertRange.insertNode(space);
  insertRange.setStartAfter(space);
  insertRange.collapse(true);
  sel.removeAllRanges();
  sel.addRange(insertRange);
}

// ── Hook ──────────────────────────────────────────────────────────────────────
function getCaretRect() {
  const sel = window.getSelection();
  if (sel && sel.rangeCount > 0) {
    const r = sel.getRangeAt(0).getBoundingClientRect();
    if (r.height > 0) return r;
  }
  return null;
}

export function useMentionInContentEditable(editorRef, members) {
  const [dropdown, setDropdown] = useState(null);
  // dropdown = { results: [...], index: number }
  const [caretPos, setCaretPos] = useState(null); // { top, left } in viewport px

  const membersRef = useRef(members);
  membersRef.current = members;

  const captureCaretPos = useCallback(() => {
    const r = getCaretRect();
    if (r) setCaretPos({ top: r.bottom + 4, left: r.left });
  }, []);

  const checkMention = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const textBefore = getTextBeforeCursor(el);
    // Match @ at start or after whitespace, followed by word chars (or empty)
    const match = textBefore.match(/(?:^|[^@\w])(@\w*)$/);
    if (!match) { setDropdown(null); setCaretPos(null); return; }
    const query = match[1].slice(1).toLowerCase();
    const results = membersRef.current.filter(m => {
      const name = memberDisplayName(m.first_name, m.last_name, m.email).toLowerCase();
      return name.includes(query) || (m.email || '').toLowerCase().includes(query);
    });
    if (results.length === 0) { setDropdown(null); setCaretPos(null); return; }
    captureCaretPos();
    setDropdown(prev => ({ results, index: prev?.index ?? 0 }));
  }, [editorRef, captureCaretPos]);

  // Re-read caret position on scroll so the dropdown tracks the editor
  useEffect(() => {
    if (!dropdown) return;
    const el = editorRef.current;
    if (!el) return;
    // Walk up to find all scrollable ancestors inside the document
    const scrollables = [];
    let node = el.parentElement;
    while (node && node !== document.body) {
      const { overflowY } = window.getComputedStyle(node);
      if (overflowY === 'auto' || overflowY === 'scroll') scrollables.push(node);
      node = node.parentElement;
    }
    if (scrollables.length === 0) scrollables.push(window);

    const onScroll = () => {
      const r = getCaretRect();
      if (r) setCaretPos({ top: r.bottom + 4, left: r.left });
      else {
        // caret rect collapsed to zero when scrolled out of view — close
        setDropdown(null);
        setCaretPos(null);
      }
    };

    scrollables.forEach(s => s.addEventListener('scroll', onScroll, { passive: true }));
    return () => scrollables.forEach(s => s.removeEventListener('scroll', onScroll));
  }, [dropdown, editorRef]);

  const handleMentionInput = useCallback(() => {
    checkMention();
  }, [checkMention]);

  const closeMention = useCallback(() => setDropdown(null), []);

  const selectMention = useCallback((member) => {
    const el = editorRef.current;
    if (!el) return;
    insertMentionAtCursor(member, null, el);
    setDropdown(null);
    el.dispatchEvent(new Event('input', { bubbles: true }));
  }, [editorRef]);

  const handleMentionKeyDown = useCallback((e) => {
    if (!dropdown || dropdown.results.length === 0) return false;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setDropdown(d => ({ ...d, index: (d.index + 1) % d.results.length }));
      return true;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setDropdown(d => ({ ...d, index: (d.index - 1 + d.results.length) % d.results.length }));
      return true;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      const sel = dropdown.results[dropdown.index];
      if (sel) { e.preventDefault(); selectMention(sel); return true; }
    }
    if (e.key === 'Escape') {
      setDropdown(null);
      return true;
    }
    return false;
  }, [dropdown, selectMention]);

  const MentionDropdown = useCallback(() => {
    if (!dropdown || dropdown.results.length === 0) return null;
    const el = editorRef.current;
    const fallbackRect = el?.getBoundingClientRect();
    const pos = caretPos ?? (fallbackRect ? { top: fallbackRect.bottom + 4, left: fallbackRect.left } : null);

    let style;
    if (pos) {
      const dropdownHeight = Math.min(dropdown.results.length * 44, 300);
      const dropdownWidth = 280; // matches maxWidth below
      const spaceBelow = window.innerHeight - pos.top;
      const caretTop = pos.top - 4; // original caret bottom, 4px gap was added
      // Clamp left so the dropdown (up to maxWidth) never overflows past
      // either edge of the viewport, even on a very narrow window.
      const left = Math.max(8, Math.min(pos.left, window.innerWidth - dropdownWidth - 8));
      if (spaceBelow < dropdownHeight + 8 && caretTop > dropdownHeight + 8) {
        // flip above
        style = { position: 'fixed', bottom: window.innerHeight - caretTop + 4, left, top: 'auto', right: 'auto', transform: 'none', zIndex: 9000, minWidth: 200, maxWidth: 280 };
      } else {
        style = { position: 'fixed', top: pos.top, left, bottom: 'auto', right: 'auto', transform: 'none', zIndex: 9000, minWidth: 200, maxWidth: 280 };
      }
    } else {
      style = { position: 'absolute', bottom: '100%', left: 0, zIndex: 9000, marginBottom: 4 };
    }
    return (
      <ul className="mention-dropdown" style={style}>
        {dropdown.results.map((m, i) => {
          const name = memberDisplayName(m.first_name, m.last_name, m.email);
          return (
            <li
              key={m.user_id}
              className={`mention-option${i === dropdown.index ? ' active' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); selectMention(m); }}
            >
              <span className="mention-avatar">{name.slice(0, 1).toUpperCase()}</span>
              <span className="mention-name">{name}</span>
            </li>
          );
        })}
      </ul>
    );
  }, [dropdown, caretPos, selectMention, editorRef]);

  return { dropdown, handleMentionKeyDown, handleMentionInput, closeMention, MentionDropdown };
}
